import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { type Session, type User } from '@supabase/supabase-js';
import { AuthContext, getSupabaseClient, type AuthContextValue } from '../lib/auth';

function extractRole(appMetadata: Record<string, unknown>): string | undefined {
  const role = appMetadata['role'];
  return typeof role === 'string' ? role : undefined;
}

interface AuthProviderProps {
  readonly children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    void (async () => {
      const supabase = await getSupabaseClient();
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);

      const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
      });

      cleanup = () => listener.subscription.unsubscribe();
    })();

    return () => cleanup?.();
  }, []);

  // Bounce non-superadmins immediately if a session ever lands here.
  // We can't check the role before authenticating (that would require an admin
  // Supabase API key on the client), so we enforce after the session lands.
  useEffect(() => {
    if (!user) return;
    const role = extractRole(user.app_metadata ?? {});
    if (role !== 'superadmin') {
      void (async () => {
        const supabase = await getSupabaseClient();
        await supabase.auth.signOut();
        window.location.replace(`/login?error=${encodeURIComponent(
          'Access denied. This portal is for superadmins only.',
        )}`);
      })();
    }
  }, [user]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<{ error: string | null }> => {
      const trimmed = email.trim();
      if (!trimmed) return { error: 'Email is required.' };
      if (!password) return { error: 'Password is required.' };
      const supabase = await getSupabaseClient();
      const { error } = await supabase.auth.signInWithPassword({
        email: trimmed,
        password,
      });
      if (error) return { error: error.message };
      return { error: null };
    },
    [],
  );

  const signOut = useCallback(async () => {
    const supabase = await getSupabaseClient();
    // Deliberately does NOT touch the superadmin's "view as" selection. That
    // belongs to their web-app login (a different session), and clearing it
    // from here would silently drop them out of a company they are actively
    // working in from another tab.
    // Global sign-out revokes the session server-side, but that needs a still-valid
    // token. If the session has already expired (server returns 403), fall back to a
    // local sign-out so an expired session can't trap the user on the page.
    try {
      const { error } = await supabase.auth.signOut();
      if (error) await supabase.auth.signOut({ scope: 'local' });
    } catch {
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        // last resort — nuke any persisted Supabase session so the UI logs out
        try {
          for (const k of Object.keys(localStorage)) {
            if (k.startsWith('sb-') && k.endsWith('-auth-token')) localStorage.removeItem(k);
          }
        } catch { /* noop */ }
      }
    }
  }, []);

  const value: AuthContextValue = { user, session, loading, signIn, signOut };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
